import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  doc, getDoc, writeBatch, serverTimestamp, updateDoc,
  collection, setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../../context/CompanyContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import BusinessNumberStep from '../../components/onboarding/BusinessNumberStep';
import {
  Building2, Phone, CreditCard, Send, CheckCircle2,
  ArrowRight, Loader2, Plus, Trash2, AlertTriangle,
  ChevronLeft, ExternalLink,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';
const emptyItem = () => ({ id: Date.now() + Math.random(), description: '', price: '' });

// ── Progress bar ─────────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  if (step < 1 || step > 4) return null;
  const labels = ['Your Business', 'Business Number', 'Connect Stripe', 'First Quote'];
  const pct = (step / 4) * 100;
  return (
    <div className="mb-6">
      <div className="flex justify-between mb-2 px-1">
        {labels.map((l, i) => (
          <span
            key={l}
            className={`text-xs transition-colors ${step >= i + 1 ? 'text-orange-400 font-medium' : 'text-slate-500'}`}
          >
            {l}
          </span>
        ))}
      </div>
      <div className="h-1 bg-slate-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-orange-500 transition-all duration-500 rounded-full"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Onboarding() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { companyId } = useCompany();

  // ── Global step state ────────────────────────────────────────────────────────
  const [step, setStep] = useState(0); // 0=welcome 1=business 2=number 3=stripe 4=quote

  // ── Step 1: business info ────────────────────────────────────────────────────
  const [companyName, setCompanyName]   = useState('');
  const [phone, setPhone]               = useState('');
  const [logoFile, setLogoFile]         = useState(null);
  const [brandColor, setBrandColor]     = useState('#0057B8');
  const [savingStep1, setSavingStep1]   = useState(false);

  // ── Step 2: stripe connect ───────────────────────────────────────────────────
  const [stripeStatus, setStripeStatus]   = useState(null); // null|'active'|'pending'|'not_connected'
  const [stripeLoading, setStripeLoading] = useState(false);
  const [connecting, setConnecting]       = useState(false);
  const [skipWarning, setSkipWarning]     = useState(false);
  const [skippingStripe, setSkippingStripe] = useState(false);

  // ── Step 3: quote form ───────────────────────────────────────────────────────
  const [customerName, setCustomerName]   = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [items, setItems]                 = useState([emptyItem()]);
  const [notes, setNotes]                 = useState('');
  const [sendSms, setSendSms]             = useState(true);
  const [submitting, setSubmitting]       = useState(false);
  const [quoteSent, setQuoteSent]         = useState(null); // { quoteId, name, viaSms }

  // ── On mount: detect Stripe redirect return params ────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stepParam = parseInt(params.get('step') || '0', 10);
    const stripeParam = params.get('stripe');
    if (stepParam >= 1 && stepParam <= 4) setStep(stepParam);
    if (stripeParam === 'connected') {
      setStripeStatus('active');
      toast.success('Stripe connected! Ready to accept payments.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (stripeParam === 'refresh') {
      toast.info('Stripe setup was not completed. Click "Connect Stripe" to try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // ── Pre-load company data to pre-fill Step 1 ─────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    getDoc(doc(db, 'companies', companyId))
      .then(snap => {
        const d = snap.data() || {};
        if (d.name)                         setCompanyName(d.name);
        if (d.contact?.phone)               setPhone(d.contact.phone);
        if (d.branding?.brandColorPrimary)  setBrandColor(d.branding.brandColorPrimary);
      })
      .catch(() => {});
  }, [companyId]);

  // ── Load Stripe status when entering Step 2 ───────────────────────────────────
  useEffect(() => {
    if (step !== 3 || !companyId || !user || stripeStatus) return;
    const load = async () => {
      setStripeLoading(true);
      try {
        const token = await user.getIdToken();
        const res   = await fetch(`${API}/api/stripe/connect/status?companyId=${companyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        const s = data.status || 'not_connected';
        setStripeStatus(s);
        if (s === 'active') {
          // Already connected — auto-advance after showing success briefly
          setTimeout(() => setStep(4), 1200);
        }
      } catch {
        setStripeStatus('not_connected');
      } finally {
        setStripeLoading(false);
      }
    };
    load();
  }, [step, companyId, user, stripeStatus]);

  // ── Computed (quote total) ────────────────────────────────────────────────────
  const quoteTotal = items.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);
  const addItem    = () => setItems(p => [...p, emptyItem()]);
  const removeItem = (id) => setItems(p => p.filter(i => i.id !== id));
  const updateItem = (id, field, val) =>
    setItems(p => p.map(i => i.id === id ? { ...i, [field]: val } : i));

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const finishOnboarding = async () => {
    try {
      const batch = writeBatch(db);
      if (user?.uid) {
        batch.update(doc(db, 'users', user.uid), {
          onboardingComplete: true,
          updatedAt: serverTimestamp(),
        });
        await batch.commit();
      }
    } catch {}
    // Number provisioning is now a deliberate choice in the Business Number
    // step — never auto-provision on finish/resume (would create duplicate
    // numbers on re-entry).
    // Sprint 306 — never-subscribed owner routing.
    // Send a brand-new owner straight to /pricing after they finish
    // onboarding. Existing lapsed/canceled tenants (canWrite=false but
    // subscription EXISTS) must stay on /dashboard in read-only mode
    // — we only redirect to pricing when the tenant has never had any
    // subscription mirror written. AppReadyGate still handles the
    // in-app enforcement afterwards.
    let goToPricing = false;
    try {
      const token = await auth.currentUser?.getIdToken();
      if (token) {
        const resp = await fetch(`${API}/api/billing/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const body = await resp.json();
          if (!body?.subscription) goToPricing = true;   // never subscribed
        }
      }
    } catch { /* non-fatal — fall through to /dashboard */ }
    navigate(goToPricing ? '/pricing' : '/dashboard', { replace: true });
  };

  const handleStep1Save = async () => {
    if (!companyName.trim()) { toast.error('Please enter your company name'); return; }
    if (!companyId || !user?.uid) { toast.error('Session expired. Please refresh.'); return; }
    setSavingStep1(true);
    try {
      const update = { name: companyName.trim(), updatedAt: serverTimestamp() };
      if (phone.trim())  update['contact.phone'] = phone.trim();
      if (brandColor)    update['branding.brandColorPrimary'] = brandColor;
      if (logoFile && storage) {
        try {
          const sRef   = ref(storage, `companies/${companyId}/branding/logo`);
          await uploadBytes(sRef, logoFile);
          update['branding.logoUrl'] = await getDownloadURL(sRef);
        } catch {}
      }
      await updateDoc(doc(db, 'companies', companyId), update);
      setStep(2);
    } catch {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSavingStep1(false);
    }
  };

  const handleStripeConnect = async () => {
    if (!companyId || !user) return;
    setConnecting(true);
    try {
      const token = await user.getIdToken();
      const res   = await fetch(`${API}/api/stripe/connect/create-link`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ companyId, return_to: 'onboarding' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create link');
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message || 'Failed to start Stripe setup');
      setConnecting(false);
    }
  };

  const handleStripeSkip = async () => {
    setSkippingStripe(true);
    try {
      if (companyId) {
        await updateDoc(doc(db, 'companies', companyId), {
          stripeConnectSkipped: true,
          updatedAt: serverTimestamp(),
        });
      }
    } catch {}
    setSkippingStripe(false);
    setStep(3);
  };

  const handleQuoteSubmit = async () => {
    if (!customerName.trim())                        { toast.error('Customer name is required'); return; }
    if (sendSms && !customerPhone.trim())             { toast.error('Phone number is required to send via SMS'); return; }
    const validItems = items.filter(i => i.description.trim() && parseFloat(i.price) >= 0);
    if (!validItems.length)                          { toast.error('Add at least one service item'); return; }

    setSubmitting(true);
    try {
      const qRef  = doc(collection(db, 'companies', companyId, 'quotes'));
      const qId   = qRef.id;
      const total = validItems.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);

      await setDoc(qRef, {
        quoteId:   qId,
        companyId,
        status:    'draft',
        createdBy: auth.currentUser?.uid || '',
        client:    { name: customerName.trim(), phone: customerPhone.trim() },
        lineItems: validItems.map(i => ({
          description: i.description.trim(),
          quantity:    1,
          unitPrice:   parseFloat(i.price) || 0,
          total:       parseFloat(i.price) || 0,
        })),
        notes:     notes.trim(),
        total,
        totals:    { subtotal: total, grandTotal: total },
        source:    'onboarding_quick_quote',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sentAt:    null,
        viewedAt:  null,
      });

      if (sendSms && customerPhone.trim()) {
        const token = await auth.currentUser?.getIdToken();
        const res   = await fetch(`${API}/api/quotes/${qId}/send-sms`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body:    JSON.stringify({ companyId }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || 'SMS send failed');
        }
      }
      setQuoteSent({ quoteId: qId, name: customerName.trim(), viaSms: sendSms });
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetQuote = () => {
    setQuoteSent(null);
    setCustomerName('');
    setCustomerPhone('');
    setItems([emptyItem()]);
    setNotes('');
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <ProgressBar step={step} />

        <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">

          {/* ── STEP 0: Welcome ────────────────────────────────────────────── */}
          {step === 0 && (
            <div className="p-8 space-y-6 text-center" data-testid="onboarding-welcome">
              <div className="space-y-3">
                <div className="w-14 h-14 bg-orange-500/20 rounded-2xl flex items-center justify-center mx-auto">
                  <span className="text-2xl font-black text-orange-400">K</span>
                </div>
                <h1 className="text-2xl font-bold text-white">
                  Welcome to KRO{profile?.displayName ? `, ${profile.displayName.split(' ')[0]}` : ''}
                </h1>
                <p className="text-slate-400 text-sm leading-relaxed max-w-xs mx-auto">
                  Let's get your first quote out in 5 minutes. We'll walk you through everything.
                </p>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={() => setStep(1)}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-base"
                  data-testid="onboarding-start-btn"
                >
                  Let's get started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <button
                  onClick={finishOnboarding}
                  className="w-full text-sm text-slate-500 hover:text-slate-300 transition-colors py-2"
                  data-testid="onboarding-skip-welcome"
                >
                  Skip — I'll explore on my own
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 1: Your Business ───────────────────────────────────────── */}
          {step === 1 && (
            <div className="p-8 space-y-5" data-testid="onboarding-step-1">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-5 h-5 text-orange-400" />
                  <h2 className="text-xl font-bold text-white">Your Business</h2>
                </div>
                <p className="text-slate-400 text-sm">This appears on every quote you send.</p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm font-medium">
                  Company Name <span className="text-orange-400">*</span>
                </Label>
                <Input
                  data-testid="onboarding-company-name"
                  placeholder="Acme Roof Cleaning Co."
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 h-11"
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm font-medium flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" /> Business Phone
                  <span className="text-slate-500 text-xs font-normal">(optional)</span>
                </Label>
                <Input
                  data-testid="onboarding-phone"
                  placeholder="(555) 000-0000"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 h-11"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm font-medium">
                    Logo <span className="text-slate-500 text-xs font-normal">(optional)</span>
                  </Label>
                  <input
                    data-testid="onboarding-logo"
                    type="file"
                    accept="image/*"
                    onChange={e => setLogoFile(e.target.files?.[0] || null)}
                    className="w-full text-sm text-slate-400 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:bg-orange-500 file:text-white hover:file:bg-orange-600 cursor-pointer"
                  />
                  {logoFile && <p className="text-xs text-emerald-400 truncate">{logoFile.name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-sm font-medium">
                    Brand Color <span className="text-slate-500 text-xs font-normal">(optional)</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="onboarding-brand-color"
                      type="color"
                      value={brandColor}
                      onChange={e => setBrandColor(e.target.value)}
                      className="w-10 h-9 rounded border border-slate-600 bg-slate-700 cursor-pointer p-0.5"
                    />
                    <span className="text-slate-400 text-sm font-mono">{brandColor}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleStep1Save}
                disabled={savingStep1 || !companyName.trim()}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2.5"
                data-testid="onboarding-step1-continue"
              >
                {savingStep1
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                  : <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>
                }
              </Button>
              <button
                onClick={finishOnboarding}
                className="w-full text-xs text-slate-600 hover:text-slate-400 transition-colors py-1"
              >
                Skip setup
              </button>
            </div>
          )}

          {/* ── STEP 2: Business Number ─────────────────────────────────────── */}
          {step === 2 && (
            <BusinessNumberStep
              onBack={() => setStep(1)}
              onContinue={() => setStep(3)}
            />
          )}

          {/* ── STEP 3: Stripe Connect ──────────────────────────────────────── */}
          {step === 3 && (
            <div className="p-8 space-y-5" data-testid="onboarding-step-2">
              <div>
                <button
                  onClick={() => setStep(2)}
                  className="flex items-center gap-1 text-slate-400 hover:text-slate-200 text-sm mb-4 -ml-1 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-5 h-5 text-orange-400" />
                  <h2 className="text-xl font-bold text-white">Connect Stripe</h2>
                </div>
                <p className="text-slate-400 text-sm">
                  So clients can pay the moment they approve your quote.
                </p>
              </div>

              {/* Visual preview of what clients see */}
              <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
                <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-3">
                  What your client sees after approving
                </p>
                <div className="bg-white rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-green-700 font-medium bg-green-50 border border-green-200 rounded-md px-3 py-2">
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                    Quote Accepted — secure your booking:
                  </div>
                  <div className="h-9 bg-slate-800 rounded-md flex items-center justify-center text-xs text-white font-medium">
                    Secure Your Spot – Pay Deposit ($75)
                  </div>
                  <div className="h-9 border border-gray-200 rounded-md flex items-center justify-center text-xs text-gray-600">
                    Pay in Full ($300)
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Payments go directly to your Stripe account
                </p>
              </div>

              {/* Loading */}
              {stripeLoading && (
                <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Checking Stripe status…
                </div>
              )}

              {/* Already connected */}
              {!stripeLoading && stripeStatus === 'active' && (
                <div
                  className="bg-emerald-900/30 border border-emerald-700 rounded-xl p-4 text-center"
                  data-testid="onboarding-stripe-connected"
                >
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                  <p className="text-emerald-300 font-semibold text-sm">Stripe is connected!</p>
                  <p className="text-slate-400 text-xs mt-1">Advancing to your first quote…</p>
                </div>
              )}

              {/* Not yet connected */}
              {!stripeLoading && stripeStatus !== 'active' && (
                <>
                  <Button
                    onClick={handleStripeConnect}
                    disabled={connecting}
                    className="w-full bg-[#635BFF] hover:bg-[#4F46E5] text-white py-3 text-sm font-semibold"
                    data-testid="onboarding-stripe-connect-btn"
                  >
                    {connecting
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connecting…</>
                      : <><ExternalLink className="w-4 h-4 mr-2" />Connect Stripe</>
                    }
                  </Button>

                  {/* Skip flow: first click shows warning, second click confirms */}
                  {!skipWarning ? (
                    <button
                      onClick={() => setSkipWarning(true)}
                      className="w-full text-sm text-slate-500 hover:text-slate-300 transition-colors py-1"
                      data-testid="onboarding-stripe-skip-trigger"
                    >
                      Skip for now
                    </button>
                  ) : (
                    <div
                      className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-4 space-y-3"
                      data-testid="onboarding-stripe-skip-warning"
                    >
                      <div className="flex items-start gap-2.5">
                        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-amber-300 text-sm font-medium leading-snug">
                            Clients won't be able to pay online
                          </p>
                          <p className="text-slate-400 text-xs mt-1.5 leading-relaxed">
                            You can still send quotes, but the payment buttons won't work until
                            Stripe is connected. You can do this anytime in{' '}
                            <strong className="text-slate-300">Settings → Integrations</strong>.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleStripeSkip}
                        disabled={skippingStripe}
                        className="w-full text-sm text-amber-400 hover:text-amber-200 border border-amber-700/40 rounded-lg py-2 transition-colors disabled:opacity-50"
                        data-testid="onboarding-stripe-skip-confirm"
                      >
                        {skippingStripe ? 'Continuing…' : "I understand — I'll connect Stripe later"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── STEP 4: First Quote ─────────────────────────────────────────── */}
          {step === 4 && !quoteSent && (
            <div className="p-8 space-y-5" data-testid="onboarding-step-3">
              <div>
                <button
                  onClick={() => setStep(3)}
                  className="flex items-center gap-1 text-slate-400 hover:text-slate-200 text-sm mb-4 -ml-1 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <div className="flex items-center gap-2 mb-1">
                  <Send className="w-5 h-5 text-orange-400" />
                  <h2 className="text-xl font-bold text-white">Send Your First Quote</h2>
                </div>
                <p className="text-slate-400 text-sm">
                  A real quote — your customer gets a link to approve and pay.
                </p>
              </div>

              {/* Customer info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-xs font-medium">
                    Customer Name <span className="text-orange-400">*</span>
                  </Label>
                  <Input
                    data-testid="onboarding-quote-customer-name"
                    placeholder="Jane Smith"
                    value={customerName}
                    onChange={e => setCustomerName(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-xs font-medium">
                    Phone {sendSms && <span className="text-orange-400">*</span>}
                  </Label>
                  <Input
                    data-testid="onboarding-quote-customer-phone"
                    placeholder="(555) 000-0000"
                    value={customerPhone}
                    onChange={e => setCustomerPhone(e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  />
                </div>
              </div>

              {/* Line items */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-xs font-medium">Services</Label>
                {items.map((item, idx) => (
                  <div key={item.id} className="flex gap-2 items-center">
                    <Input
                      data-testid={`onboarding-quote-item-desc-${idx}`}
                      placeholder={idx === 0 ? 'e.g. Roof Cleaning' : 'Service description'}
                      value={item.description}
                      onChange={e => updateItem(item.id, 'description', e.target.value)}
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 flex-1"
                    />
                    <div className="relative w-28 flex-shrink-0">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">$</span>
                      <Input
                        data-testid={`onboarding-quote-item-price-${idx}`}
                        placeholder="0.00"
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.price}
                        onChange={e => updateItem(item.id, 'price', e.target.value)}
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 pl-7"
                      />
                    </div>
                    {items.length > 1 && (
                      <button
                        onClick={() => removeItem(item.id)}
                        className="p-1.5 text-slate-500 hover:text-red-400 transition-colors flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={addItem}
                  className="flex items-center gap-1.5 text-sm text-orange-400 hover:text-orange-300 transition-colors"
                  data-testid="onboarding-quote-add-item"
                >
                  <Plus className="w-4 h-4" /> Add another service
                </button>
              </div>

              {/* Optional note */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs font-medium">
                  Note to Customer <span className="text-slate-500 font-normal">(optional)</span>
                </Label>
                <textarea
                  data-testid="onboarding-quote-notes"
                  placeholder="Thanks for your interest! This quote is valid for 30 days."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  className="w-full rounded-md bg-slate-700 border border-slate-600 text-white placeholder:text-slate-500 text-sm px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-orange-500"
                />
              </div>

              {/* Total display */}
              <div className="bg-slate-900 rounded-xl p-3 flex items-center justify-between">
                <span className="text-slate-400 text-sm">Total</span>
                <span className="text-white font-bold text-lg" data-testid="onboarding-quote-total">
                  ${quoteTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>

              {/* SMS toggle */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSendSms(s => !s)}
                  data-testid="onboarding-quote-sms-toggle"
                  className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 relative ${sendSms ? 'bg-orange-500' : 'bg-slate-600'}`}
                >
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${sendSms ? 'translate-x-5' : 'translate-x-1'}`} />
                </button>
                <span className="text-slate-300 text-sm">Send quote link via SMS</span>
              </div>

              <Button
                onClick={handleQuoteSubmit}
                disabled={submitting}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 text-sm font-semibold"
                data-testid="onboarding-quote-send-btn"
              >
                {submitting
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
                  : <><Send className="w-4 h-4 mr-2" />Send Quote via SMS</>
                }
              </Button>
              <button
                onClick={finishOnboarding}
                className="w-full text-xs text-slate-600 hover:text-slate-400 transition-colors py-1"
                data-testid="onboarding-quote-skip"
              >
                I'll do this later → Go to dashboard
              </button>
            </div>
          )}

          {/* ── STEP 4: Quote sent success ─────────────────────────────────── */}
          {step === 4 && quoteSent && (
            <div className="p-8 text-center space-y-5" data-testid="onboarding-quote-success">
              <div className="w-16 h-16 bg-emerald-900/40 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {quoteSent.viaSms ? 'Quote Sent!' : 'Quote Created!'}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed max-w-xs mx-auto">
                  {quoteSent.viaSms
                    ? `${quoteSent.name} will receive your quote via SMS. When they approve and pay, you'll see it in your dashboard.`
                    : `Quote saved for ${quoteSent.name}. Share the link from your Quotes page when you're ready.`
                  }
                </p>
              </div>
              <Button
                onClick={finishOnboarding}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 font-semibold"
                data-testid="onboarding-go-to-dashboard"
              >
                Go to my dashboard
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <button
                onClick={resetQuote}
                className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
              >
                Send another quote
              </button>
            </div>
          )}

        </div>

        <p className="text-slate-600 text-xs text-center mt-4">
          All settings can be updated anytime in Settings.
        </p>
      </div>
    </div>
  );
}
